import React from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DropdownField from '../../DropDownField/DropDownField';
import RequiredField from '../../../SharedComponents/RequiredField/RequiredField';
import { COUNTRIES } from '../../../../Constants/countries';
import { GENDER, STAFF_STATUS } from '../../../../Constants/constants';
import { REQUIRED_FIELDS } from '../../MainForm/config/formConfig';

const StaffInfoSection = ({ staffData, handleChange, errors = {}, showValidation = false, readOnly = false }) => {
    const { t } = useTranslation('staffInformation');

    // Helper to check if a field is required
    const isFieldRequired = (fieldName) => {
        return REQUIRED_FIELDS?.staff_info?.[fieldName] === 1.0;
    };

    // Helper to check if field is empty
    const isFieldEmpty = (fieldName) => {
        const value = staffData[fieldName];
        return !value || value.toString().trim() === '';
    };

    // Helper to determine if a field should show error styling
    const shouldShowError = (fieldName) => {
        // Show error if:
        // 1. There's a validation error for this field, OR
        // 2. Field is required and empty (for real-time visual feedback)
        return errors[fieldName] || (isFieldRequired(fieldName) && isFieldEmpty(fieldName));
    };

    // Get field error message
    const getErrorMessage = (fieldName) => {
        if (errors[fieldName]) {
            return errors[fieldName];
        }
        if (isFieldRequired(fieldName) && isFieldEmpty(fieldName)) {
            return t('staffInformationForm.errors.fieldRequired', { field: fieldName.replace(/_/g, ' ') });
        }
        return '';
    };

    // Custom styles for required field borders
    const getFieldStyle = (fieldName) => {
        if (readOnly) return {};

        if (shouldShowError(fieldName)) {
            return {
                borderColor: '#dc3545',
                borderWidth: '2px',
                boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
            };
        }
        return {};
    };

    return (
        <>
            <Row className="mb-3">
                {/* Gender */}
                <Col md={6}>
                    <DropdownField
                        controlId="gender"
                        label={`${t('staffInfo.gender')}${isFieldRequired('gender') ? ' *' : ''}`}
                        name="gender"
                        value={staffData.gender || ''}
                        handleChange={handleChange}
                        options={GENDER}
                        isInvalid={shouldShowError('gender')}
                        style={getFieldStyle('gender')}
                        disabled={readOnly}
                    />
                    {shouldShowError('gender') && (
                        <Form.Text className="text-danger">
                            {getErrorMessage('gender')}
                        </Form.Text>
                    )}
                </Col>

                {/* Nationality */}
                <Col md={6}>
                    <DropdownField
                        controlId="nationality"
                        label={`${t('staffInfo.nationality')}${isFieldRequired('nationality') ? ' *' : ''}`}
                        name="nationality"
                        value={staffData.nationality || ''}
                        handleChange={handleChange}
                        options={COUNTRIES}
                        isRequired={isFieldRequired('nationality')}
                        isInvalid={shouldShowError('nationality')}
                        style={getFieldStyle('nationality')}
                        disabled={readOnly}
                    />
                    {shouldShowError('nationality') && (
                        <Form.Text className="text-danger">
                            {getErrorMessage('nationality')}
                        </Form.Text>
                    )}
                </Col>
            </Row>

            <Row className="mb-3">
                {/* Index Number */}
                <Col md={6}>
                    <RequiredField
                        label={`${t('staffInfo.indexNumber')}${isFieldRequired('index_number') ? ' *' : ''}`}
                        name="index_number"
                        value={staffData.index_number || ''}
                        onChange={handleChange}
                        placeholder={t('staffInfo.indexNumberPlaceholder')}
                        isInvalid={shouldShowError('index_number')}
                        errorText={getErrorMessage('index_number')}
                        style={getFieldStyle('index_number')}
                        readOnly={readOnly}
                    />
                </Col>

                {/* UN ID */}
                <Col md={6}>
                    <RequiredField
                        label={`${t('staffInfo.unId')}${isFieldRequired('un_id') ? ' *' : ''}`}
                        name="un_id"
                        value={staffData.un_id || ''}
                        onChange={handleChange}
                        placeholder={t('staffInfo.unIdPlaceholder')}
                        isInvalid={shouldShowError('un_id')}
                        errorText={getErrorMessage('un_id')}
                        style={getFieldStyle('un_id')}
                        readOnly={readOnly}
                    />
                </Col>
            </Row>

            <Row className="mb-3">
                {/* UN ID Expiration Date */}
                <Col md={6}>
                    <RequiredField
                        label={`${t('staffInfo.unIdExpirationDate')}${isFieldRequired('un_id_expiration_date') ? ' *' : ''}`}
                        name="un_id_expiration_date"
                        type="date"
                        value={staffData.un_id_expiration_date || ''}
                        onChange={handleChange}
                        isInvalid={shouldShowError('un_id_expiration_date')}
                        errorText={getErrorMessage('un_id_expiration_date')}
                        style={getFieldStyle('un_id_expiration_date')}
                        readOnly={readOnly}
                    />
                </Col>

                {/* Staff Status */}
                <Col md={6}>
                    <DropdownField
                        controlId="staff_status"
                        label={`${t('staffInfo.staffStatus')}${isFieldRequired('staff_status') ? ' *' : ''}`}
                        name="staff_status"
                        value={staffData.staff_status || ''}
                        handleChange={handleChange}
                        options={STAFF_STATUS}
                        isRequired={isFieldRequired('staff_status')}
                        isInvalid={shouldShowError('staff_status')}
                        style={getFieldStyle('staff_status')}
                        disabled={readOnly}
                    />
                    {shouldShowError('staff_status') && (
                        <Form.Text className="text-danger">
                            {getErrorMessage('staff_status')}
                        </Form.Text>
                    )}
                </Col>
            </Row>

            <Row className="mb-3">
                {/* Dependants */}
                <Col md={6}>
                    <RequiredField
                        label={`${t('staffInfo.dependants')}${isFieldRequired('dependants') ? ' *' : ''}`}
                        name="dependants"
                        type="number"
                        value={staffData.dependants || 0}
                        onChange={handleChange}
                        placeholder={t('staffInfo.dependantsPlaceholder')}
                        isInvalid={shouldShowError('dependants')}
                        errorText={getErrorMessage('dependants')}
                        style={getFieldStyle('dependants')}
                        readOnly={readOnly}
                    />
                </Col>

                {/* Date of Birth */}
                <Col md={6}>
                    <RequiredField
                        label={`${t('staffInfo.dateOfBirth')}${isFieldRequired('date_of_birth') ? ' *' : ''}`}
                        name="date_of_birth"
                        type="date"
                        value={staffData.date_of_birth || ''}
                        onChange={handleChange}
                        isInvalid={shouldShowError('date_of_birth')}
                        errorText={getErrorMessage('date_of_birth')}
                        style={getFieldStyle('date_of_birth')}
                        readOnly={readOnly}
                    />
                </Col>
            </Row>

            {/* Home Address */}
            <Row className="mb-3">
                <Col md={12}>
                    <RequiredField
                        label={`${t('staffInfo.homeAddress')}${isFieldRequired('home_address') ? ' *' : ''}`}
                        name="home_address"
                        as="textarea"
                        rows={2}
                        value={staffData.home_address || ''}
                        onChange={handleChange}
                        placeholder={t('staffInfo.homeAddressPlaceholder')}
                        isInvalid={shouldShowError('home_address')}
                        errorText={getErrorMessage('home_address')}
                        style={getFieldStyle('home_address')}
                        readOnly={readOnly}
                    />
                </Col>
            </Row>
        </>
    );
};

export default StaffInfoSection;